import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CachedPartnershipInstagram {
  id: string;
  user_id: string;
  ad_account_id: string;
  instagram_id: string;
  username: string;
  display_name: string;
  creator_fb_page_id: string | null;
  source_instagram_id: string | null;
  source_instagram_username: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export interface CachedPartnershipFacebookPage {
  id: string;
  user_id: string;
  ad_account_id: string;
  page_id: string;
  page_name: string;
  display_name: string;
  source_instagram_username: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export function useCachedPartnershipInstagram(userId: string | undefined, adAccountId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['cached-partnership-instagram', userId, adAccountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meta_cached_partnership_instagram')
        .select('*')
        .eq('user_id', userId!)
        .eq('ad_account_id', adAccountId!)
        .order('username');
      
      if (error) throw error;
      return data as CachedPartnershipInstagram[];
    },
    enabled: !!userId && !!adAccountId,
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['cached-partnership-instagram', userId, adAccountId] });
  };

  return { ...query, invalidate };
}

export function useCachedPartnershipFacebookPages(userId: string | undefined, adAccountId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['cached-partnership-facebook-pages', userId, adAccountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meta_cached_partnership_facebook_pages')
        .select('*')
        .eq('user_id', userId!)
        .eq('ad_account_id', adAccountId!)
        .order('page_name');
      
      if (error) throw error;
      return data as CachedPartnershipFacebookPage[];
    },
    enabled: !!userId && !!adAccountId,
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['cached-partnership-facebook-pages', userId, adAccountId] });
  };

  return { ...query, invalidate };
}
